import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SalesCard, type SalesItem } from "@/components/SalesCard";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { calculateDistance } from "@/lib/geolocation";
import { regions, australianCities } from "@shared/schema";
import { ShoppingCart, MapPin, Calendar } from "lucide-react";

// Phase 3.7: D-Day 계산 함수
function calculateDDay(departureDate: string): number | null {
  if (!departureDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const departure = new Date(departureDate);
  departure.setHours(0, 0, 0, 0);
  const diffTime = departure.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export default function Sales() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { selectedZoneId } = useZone();
  const { userLocation } = useLocationContext();
  const [selectedState, setSelectedState] = useState<string>(selectedRegion || "ALL");
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.7: 귀국세일 전용 필터
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [onlyBulkSale, setOnlyBulkSale] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>("dday"); // dday, latest, price

  // Phase 3.7: 귀국세일 목록 조회 (category: 'sales' 또는 market_listings에서 departureDate 필터)
  const { data: listings, isLoading } = useQuery<SalesItem[]>({
    queryKey: ["/api/unified-market", selectedZoneId, selectedState, selectedCity, "sales"],
    queryFn: async () => {
      const params = new URLSearchParams();
      // 귀국세일은 market_listings의 category='귀국세일' 또는 departureDate가 있는 항목
      params.append("category", "귀국세일");
      params.append("type", "trade");
      params.append("zone", selectedZoneId);
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/unified-market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 { ok: true, data: { items: [...] } } 형식일 수 있음
      if (data?.ok && data?.data?.items) {
        return data.data.items;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.7: 거리 계산 및 고급 필터링 로직 (D-Day 정렬)
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    // 거리 계산
    let result = listings.map((listing) => {
      let distance: number | undefined = undefined;
      if (
        userLocation &&
        listing.latitude &&
        listing.longitude &&
        typeof listing.latitude === "number" &&
        typeof listing.longitude === "number"
      ) {
        distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          listing.latitude,
          listing.longitude
        );
      }
      return { ...listing, distance };
    });

    // 필터링
    result = result.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 일괄판매 필터
      if (onlyBulkSale && !listing.bulkSale) {
        return false;
      }

      // 가격 필터
      if (minPrice || maxPrice) {
        const price = listing.priceCents;
        if (price === null || price === undefined || price === -1 || price === 0) {
          // 가격협의나 무료나눔은 필터에서 제외하지 않음
        } else {
          const priceInDollars = price / 100;
          if (minPrice && priceInDollars < parseFloat(minPrice)) return false;
          if (maxPrice && priceInDollars > parseFloat(maxPrice)) return false;
        }
      }

      return true;
    });

    // Phase 3.7: D-Day 정렬 (마감 임박순)
    return result.sort((a, b) => {
      if (sortBy === "dday") {
        const aDDay = a.departureDate ? calculateDDay(a.departureDate) : null;
        const bDDay = b.departureDate ? calculateDDay(b.departureDate) : null;

        // D-Day가 있는 항목 우선
        if (aDDay !== null && bDDay === null) return -1;
        if (aDDay === null && bDDay !== null) return 1;
        if (aDDay !== null && bDDay !== null) {
          // D-Day가 가까운 순 (음수는 뒤로)
          return aDDay - bDDay;
        }
      } else if (sortBy === "price") {
        const aPrice = a.priceCents || 0;
        const bPrice = b.priceCents || 0;
        return aPrice - bPrice;
      }

      // 최신순 정렬
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [listings, searchTerm, onlyBulkSale, minPrice, maxPrice, sortBy, userLocation]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              귀국세일
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              귀국 전 대량판매 - 빠른 거래를 위한 전용 페이지
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <ShoppingCart className="w-3 h-3" />
                귀국세일
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <Calendar className="w-3 h-3" />
                D-Day 정렬
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="제목 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.7: 가격범위 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    가격범위
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Phase 3.7: 일괄판매만 보기 */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="only-bulk-sale"
                    checked={onlyBulkSale}
                    onChange={(e) => setOnlyBulkSale(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="only-bulk-sale"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    일괄판매만 보기
                  </label>
                </div>

                {/* Phase 3.7: 정렬 옵션 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    정렬
                  </label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dday">마감 임박순 (D-Day)</SelectItem>
                      <SelectItem value="latest">최신순</SelectItem>
                      <SelectItem value="price">가격 낮은순</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/sales/register">
                    <Button className="w-full" data-testid="button-register">
                      귀국세일 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 귀국세일`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <SalesCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      window.location.href = `/chat-start/sales/${listing.id}`;
                    }}
                    onBulkBuy={() => {
                      // TODO: 일괄구매 로직
                      window.location.href = `/sales/${listing.id}?bulk=true`;
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 귀국세일이 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 귀국세일을 등록해보세요!
                  </p>
                  {user && (
                    <Link href="/sales/register">
                      <Button>귀국세일 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
